import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-rating-gauge WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, RatingGaugeWebElement.ts.
 */
export class RatingGaugeWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>max</code> property.
   * The maximum value of the gauge.
   * @return The value of <code>max</code> property.
   *
   */
  public getMax(): Promise<number> {
    return this.getProperty<number>('max');
  }

  /**
   * Gets the value of <code>readonly</code> property.
   *
   * @return The value of <code>readonly</code> property.
   *
   */
  public getReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('readonly');
  }

  /**
   * Gets the value of <code>disabled</code> property.
   *
   * @return The value of <code>disabled</code> property.
   *
   */
  public getDisabled(): Promise<boolean> {
    return this.getProperty<boolean>('disabled');
  }

  /**
   * Sets the value of <code>changed</code> property.
   * Whether there has been a value entered by the user even if it is the same as the initial value.
   * @param changed The value to set for <code>changed</code>
   *
   */
  public changeChanged(changed: boolean): Promise<void> {
    return this.setProperty<boolean>('changed', changed);
  }

  /**
   * Gets the value of <code>changed</code> property.
   * Whether there has been a value entered by the user even if it is the same as the initial value.
   * @return The value of <code>changed</code> property.
   *
   */
  public getChanged(): Promise<boolean> {
    return this.getProperty<boolean>('changed');
  }

  /**
   * Sets the value of <code>value</code> property.
   * The value of the Rating Gauge.
   * @param value The value to set for <code>value</code>
   *
   */
  public changeValue(value: number | null): Promise<void> {
    return this.setProperty<number | null>('value', value);
  }

  /**
   * Gets the value of <code>value</code> property.
   * The value of the Rating Gauge.
   * @return The value of <code>value</code> property.
   *
   */
  public getValue(): Promise<number | null> {
    return this.getProperty<number | null>('value');
  }

  /**
   * Gets the value of <code>step</code> property.
   *
   * @return The value of <code>step</code> property.
   *
   */
  public getStep(): Promise<number> {
    return this.getProperty<number>('step');
  }

  /**
   * Gets the value of <code>describedBy</code> property.
   *
   * @return The value of <code>describedBy</code> property.
   *
   */
  public getDescribedBy(): Promise<string | null> {
    return this.getProperty<string | null>('describedBy');
  }

  /**
   * Gets the value of <code>labelledBy</code> property.
   *
   * @return The value of <code>labelledBy</code> property.
   *
   */
  public getLabelledBy(): Promise<string | null> {
    return this.getProperty<string | null>('labelledBy');
  }

  /**
   * Gets the value of <code>size</code> property.
   * Specifies the size of the rating gauge items.
   * @return The value of <code>size</code> property.
   *
   */
  public getSizeProperty(): Promise<string> {
    return this.getProperty<string>('size');
  }

  /**
   * Gets the value of <code>color</code> property.
   * Specifies the color of the rating gauge items.
   * @return The value of <code>color</code> property.
   *
   */
  public getColor(): Promise<string> {
    return this.getProperty<string>('color');
  }

  /**
   * Gets the value of <code>thresholds</code> property.
   *
   * @return The value of <code>thresholds</code> property.
   *
   */
  public getThresholds(): Promise<Array<Thresholds>> {
    return this.getProperty<Array<Thresholds>>('thresholds');
  }

  /**
   * Gets the value of <code>datatip</code> property.
   *
   * @return The value of <code>datatip</code> property.
   *
   */
  public getDatatip(): Promise<null> {
    return this.getProperty<null>('datatip');
  }

  /**
   * Gets the value of <code>tooltip</code> property.
   *
   * @return The value of <code>tooltip</code> property.
   *
   */
  public getTooltip(): Promise<string> {
    return this.getProperty<string>('tooltip');
  }

  /**
   * Gets the value of <code>transientValue</code> property.
   *
   * @return The value of <code>transientValue</code> property.
   *
   */
  public getTransientValue(): Promise<number> {
    return this.getProperty<number>('transientValue');
  }
}
export interface Thresholds {
  /**
   *
   */
  accessibleLabel: string;
  /**
   *
   */
  color: string;
  /**
   *
   */
  max: number;
}
